<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="'Loading Dashboard...'" />
  </div>
  <div v-else>
    <div class="welcome" :class="{ 'welcome-empty': isEmpty }">
      <div class="welcome-orb orb1"></div>
      <div class="welcome-orb orb2"></div>
      <div class="welcome-inner">
        <div>
          <p class="welcome-greeting">
            {{ isEmpty ? 'You made it!' : greeting }}
          </p>
          <h2 class="welcome-name">
            {{
              isEmpty
                ? "Let's get started"
                : `Welcome back, ${user.preferredName}`
            }}
          </h2>
        </div>
        <div
          class="welcome-avatar pointer"
          @click="router.push({ name: 'UserAccount' })"
          role="button"
          aria-label="User Account"
        >
          <font-awesome-icon
            :icon="['fas', 'user']"
            style="color: white; font-size: 18px"
          />
        </div>
      </div>
    </div>

    <div class="quick-stats-strip">
      <StatBadge
        label="Net Worth"
        :value="formatMoney(netWorth.netWorthTotal)"
        :is-empty="isEmpty"
      />
      <div class="stat-badge">
        <span class="stat-badge-label">Month</span>
        <span class="stat-badge-value" :class="{ empty: isEmpty }">
          {{ isEmpty ? '—' : daysLeftInMonth + ' days left' }}
        </span>
      </div>
      <div
        v-if="!premiumUser"
        class="premium-card stat-badge pointer"
        @click="router.push({ name: 'UserProfile' })"
        role="button"
        aria-label="Upgrade to Premium"
      >
        <span class="premium-label">Unlock </span>
        <span class="premium-value">Go Premium ✦</span>
      </div>
      <div v-else class="badge-card stat-badge">
        <span class="premium-label" style="color: #7f77dd">Member</span>
        <span class="premium-value">✦ Premium</span>
      </div>
    </div>

    <div class="dashboard-view view">
      <DashboardCard
        title="Recent transactions"
        link-label="View all"
        @link-click="router.push({ name: 'Transactions' })"
      >
        <EmptyState
          v-if="!filteredTransactions || filteredTransactions.length === 0"
          title="No transactions yet"
          subtitle="Connect your bank to sync automatically, or add your first transaction manually."
        >
          <template #icon>
            <svg
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="4" y="4" width="64" height="64" rx="16" fill="#EEEDFE" />
              <path
                d="M36 22v16m0 0l-6-6m6 6l6-6"
                stroke="#7F77DD"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <rect
                x="18"
                y="44"
                width="36"
                height="4"
                rx="2"
                fill="#AFA9EC"
                opacity="0.5"
              />
              <rect
                x="22"
                y="50"
                width="28"
                height="4"
                rx="2"
                fill="#AFA9EC"
                opacity="0.3"
              />
            </svg>
          </template>
          <template #actions>
            <button
              class="gradient-button w-100"
              @click="router.push({ name: 'ConnectedAccounts' })"
            >
              Connect a bank account
            </button>
            <button
              class="outline-button w-100"
              @click="router.push({ name: 'Transactions' })"
            >
              Add a transaction manually
            </button>
          </template>
        </EmptyState>

        <div v-else>
          <div
            v-for="transaction in filteredTransactions"
            :key="transaction.id"
            class="txn-row"
          >
            <div class="txn-icon-wrap">
              <font-awesome-icon
                :icon="
                  transaction.transactionCategory &&
                  transaction.transactionCategory.icon
                    ? parseIconString(transaction.transactionCategory.icon)
                    : 'fa-solid fa-tag'
                "
                :style="{
                  color:
                    transaction.transactionCategory &&
                    transaction.transactionCategory.iconColor
                      ? transaction.transactionCategory.iconColor
                      : '#86c24c',
                  fontSize: '14px',
                }"
                alt="transaction category icon"
              />
            </div>
            <div class="txn-meta">
              <p class="txn-name">{{ transaction.displayName }}</p>
              <p class="txn-date">
                {{ formatDateRelative(transaction.transactionDate) }}
              </p>
            </div>
            <div
              class="txn-amount"
              :class="transaction.isIncome ? 'amount-income' : 'amount-expense'"
            >
              {{ formatMoney(transaction.amount) }}
            </div>
          </div>
        </div>
      </DashboardCard>

      <DashboardCard
        title="Top 5 categories"
        link-label="View budget"
        @link-click="router.push({ name: 'Budget' })"
      >
        <EmptyState
          v-if="!topCategoriesGraphData || topCategoriesGraphData.length < 1"
          title="No spending data yet"
          subtitle="Once you have transactions, your top categories will appear here."
        >
          <template #icon>
            <svg
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="4" y="4" width="64" height="64" rx="16" fill="#E1F5EE" />
              <rect
                x="16"
                y="46"
                width="10"
                height="14"
                rx="3"
                fill="#5DCAA5"
                opacity="0.4"
              />
              <rect
                x="31"
                y="34"
                width="10"
                height="26"
                rx="3"
                fill="#5DCAA5"
                opacity="0.65"
              />
              <rect
                x="46"
                y="22"
                width="10"
                height="38"
                rx="3"
                fill="#5DCAA5"
              />
              <path
                d="M12 20h48"
                stroke="#1D9E75"
                stroke-width="1.5"
                stroke-dasharray="3 2"
                stroke-linecap="round"
              />
            </svg>
          </template>
          <template #actions>
            <button
              class="outline-button w-100"
              @click="router.push({ name: 'Budget' })"
            >
              Set up your budget
            </button>
          </template>
        </EmptyState>

        <div v-else class="bar-chart">
          <div
            v-for="item in topCategoriesGraphData"
            :key="item.category.name"
            class="bar-row"
          >
            <span class="bar-label">{{ item.category.name }}</span>
            <div class="bar-track">
              <div
                class="bar-fill"
                :style="{
                  width: barWidth(item.categorySpent) + '%',
                  background: item.category.iconColor || '#7F77DD',
                }"
              ></div>
            </div>
            <span class="bar-value">{{ formatMoney(item.categorySpent) }}</span>
          </div>
        </div>
      </DashboardCard>

      <DashboardCard
        title="Net worth"
        link-label="View details"
        @link-click="router.push({ name: 'NetWorth' })"
      >
        <EmptyState
          v-if="
            !netWorth ||
            (netWorth.totalAssets === 0 && netWorth.totalLiabilities === 0)
          "
          title="Track your net worth"
          subtitle="Add your accounts and assets to see your complete financial picture."
        >
          <template #icon>
            <svg
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="4" y="4" width="64" height="64" rx="16" fill="#EEEDFE" />
              <circle
                cx="36"
                cy="34"
                r="14"
                stroke="#7F77DD"
                stroke-width="2.5"
                stroke-dasharray="4 2"
              />
              <path
                d="M36 26v8l5 3"
                stroke="#7F77DD"
                stroke-width="2"
                stroke-linecap="round"
              />
              <circle cx="36" cy="34" r="2.5" fill="#7F77DD" />
            </svg>
          </template>
          <template #actions>
            <button
              class="outline-button w-100"
              @click="router.push({ name: 'ConnectedAccounts' })"
            >
              Add accounts
            </button>
          </template>
        </EmptyState>

        <div v-else>
          <div class="asset-liability-grid">
            <div class="overview-card">
              <div class="text">Total Assets</div>
              <div class="net-worth-value">
                {{ formatMoney(netWorth.totalAssets) }}
              </div>
            </div>
            <div class="overview-card">
              <div class="text">Total Liabilities</div>
              <div class="net-worth-value" style="color: var(--color-expense)">
                {{ formatMoney(netWorth.totalLiabilities) }}
              </div>
            </div>
          </div>
          <div class="nw-total-card mt-1">
            <span class="nw-total-label">Total net worth</span>
            <p class="nw-total-value">
              {{ formatMoney(netWorth.netWorthTotal) }}
            </p>
          </div>
        </div>
      </DashboardCard>

      <DashboardCard
        title="Calculators"
        link-label="View all"
        @link-click="router.push({ name: 'Calculators' })"
      >
        <div class="calc-card-body">
          <p class="calc-tagline">
            Run the numbers on big decisions like mortgages, debt payoff,
            retirement, and more.
          </p>
          <div class="calc-icon-row">
            <div class="calc-icon-chip" style="background: #eeedfe">
              <font-awesome-icon
                :icon="['fas', 'house']"
                style="color: #7f77dd"
              />
            </div>
            <div class="calc-icon-chip" style="background: #e1f5ee">
              <font-awesome-icon
                :icon="['fas', 'fire']"
                style="color: #1d9e75"
              />
            </div>
            <div class="calc-icon-chip" style="background: #fef1e6">
              <font-awesome-icon
                :icon="['fas', 'piggy-bank']"
                style="color: #e89a4c"
              />
            </div>
            <div class="calc-icon-chip" style="background: #eaf2fb">
              <font-awesome-icon
                :icon="['fas', 'chart-line']"
                style="color: #4c7de8"
              />
            </div>
            <div class="calc-icon-chip calc-icon-more">+6</div>
          </div>
          <button
            class="outline-button w-100 mt-1"
            @click="router.push({ name: 'Calculators' })"
          >
            Explore calculators
          </button>
        </div>
      </DashboardCard>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../store.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import DashboardCard from '../components/DashboardCard.vue';
import EmptyState from '../components/EmptyState.vue';
import StatBadge from '../components/StatBadge.vue';
import { formatMoney } from '../utils/formatMoney.js';
import { formatDate, formatDateRelative } from '../utils/formatDate.js';

export default {
  name: 'User-Dashboard',
  components: { SkeletonLoader, DashboardCard, EmptyState, StatBadge },
  setup() {
    const router = useRouter();
    const store = useMyStore();
    const user = ref(store.user);
    let filteredTransactions = ref([]);
    let topCategoriesGraphData = ref([]);
    let netWorth = ref({
      totalAssets: 0,
      totalLiabilities: 0,
      netWorthTotal: 0,
    });
    const loading = ref(true);
    const premiumUser = computed(() => store.user.premiumSubscription);

    const daysLeftInMonth = computed(() => {
      const now = new Date();
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return lastDay.getDate() - now.getDate();
    });

    const isEmpty = computed(() => {
      return (
        (!filteredTransactions.value ||
          filteredTransactions.value.length === 0) &&
        (!topCategoriesGraphData.value ||
          topCategoriesGraphData.value.length === 0) &&
        (!netWorth.value ||
          (netWorth.value.totalAssets === 0 &&
            netWorth.value.totalLiabilities === 0))
      );
    });

    const greeting = computed(() => {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    });

    const maxSpend = computed(() => {
      if (!topCategoriesGraphData.value?.length) return 1;
      return Math.max(
        ...topCategoriesGraphData.value.map((i) => i.categorySpent || 0)
      );
    });

    const barWidth = (amount) => {
      if (!amount || maxSpend.value === 0) return 0;
      return Math.round((amount / maxSpend.value) * 100);
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      let unwatch;
      unwatch = watch(
        () => store.userLoggedIn,
        (newValue) => {
          if (newValue) {
            store
              .getUserDashboard()
              .then((response) => {
                loading.value = false;
                filteredTransactions.value =
                  response.data.mostRecentTransactions;
                topCategoriesGraphData.value = response.data.topFiveCategories;
                netWorth.value = response.data.netWorth;
              })
              .catch((error) => {
                console.error('Error fetching dashboard data:', error);
                loading.value = false;
              })
              .finally(() => {
                if (unwatch) unwatch();
              });
          }
        },
        { immediate: true }
      );
    });

    const parseIconString = (iconString) => {
      if (iconString && iconString.includes(' ')) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return ['fa-solid', 'fa-tag'];
    };

    return {
      router,
      store,
      filteredTransactions,
      parseIconString,
      topCategoriesGraphData,
      netWorth,
      formatMoney,
      loading,
      user,
      isEmpty,
      greeting,
      barWidth,
      formatDate,
      formatDateRelative,
      premiumUser,
      daysLeftInMonth,
    };
  },
};
</script>

<style scoped>
.welcome {
  background: var(--color-primary);
  padding: 1.25rem 1.25rem 2.5rem;
  position: relative;
  overflow: hidden;
}

.welcome-orb {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  pointer-events: none;
}
.orb1 {
  width: 160px;
  height: 160px;
  top: -60px;
  right: -40px;
}
.orb2 {
  width: 100px;
  height: 100px;
  bottom: -30px;
  left: 20%;
}

.welcome-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.welcome-greeting {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 2px;
  font-weight: 400;
}

.welcome-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.welcome-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background var(--transition-fast);
}
.welcome-avatar:hover {
  background: rgba(255, 255, 255, 0.25);
}

.quick-stats-strip {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  padding: 0 1rem;
  margin-top: -1.25rem;
  position: relative;
  z-index: 2;
}

.premium-card {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  border: none;
  box-shadow: 0 4px 16px rgba(127, 119, 221, 0.3);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.premium-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(127, 119, 221, 0.4);
}

.badge-card {
  background: #eeedfe;
  border: 1px solid #cecbf6;
}

.premium-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 3px;
  display: block;
  color: rgba(255, 255, 255, 0.8);
}

.premium-value {
  font-size: 0.82rem;
  font-weight: 700;
  margin: 0;
  display: block;
}

.light-theme .premium-value {
  color: var(--purple-800);
}

.dark-theme .premium-value {
  color: var(--purple-200);
}

.dashboard-view {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 1rem;
  background: transparent;
}

.txn-row {
  display: grid;
  grid-template-columns: 38px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f8f8f8;
}
.dark-theme .txn-row {
  border-bottom-color: #2a3a4e;
}
.txn-row:last-child {
  border-bottom: none;
}

.txn-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.txn-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #213547;
  margin: 0;
}
.dark-theme .txn-name {
  color: #d4d2d2;
}
.txn-date {
  font-size: 0.68rem;
  color: #aaa;
  margin: 0;
}
.txn-amount {
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-row {
  display: grid;
  grid-template-columns: 80px 1fr 56px;
  align-items: center;
  gap: 10px;
}

.bar-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: #555;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .bar-label {
  color: #aaa;
}

.bar-track {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}
.dark-theme .bar-track {
  background: #2a3a4e;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.bar-value {
  font-size: 0.7rem;
  font-weight: 600;
  color: #555;
  text-align: right;
}
.dark-theme .bar-value {
  color: #aaa;
}

.asset-liability-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.calc-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.calc-tagline {
  font-size: 0.82rem;
  color: #555;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .calc-tagline {
  color: #aaa;
}

.calc-icon-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.calc-icon-chip {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}

.calc-icon-more {
  background: #f0f0f0;
  color: #555;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.dark-theme .calc-icon-more {
  background: #2a3a4e;
  color: #aaa;
}

@media screen and (min-width: 992px) {
  .light-theme .welcome {
    background: white;
  }
  .welcome-greeting {
    color: #004751;
  }
  .welcome-name {
    color: #004751;
  }
  .welcome-avatar {
    background: #f0f0f0;
    border-color: #e0e0e0;
  }
  .welcome-avatar:hover {
    background: #e8e8e8;
  }
}

@media screen and (min-width: 1200px) {
  .dashboard-view {
    grid-template-columns: 1fr 1fr;
  }
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="'Loading Dashboard...'" />
  </div>
  <div v-else>
    <div class="welcome">
      <div class="welcome-text">
        <p>Hi {{ user.preferredName }}</p>
        <h2>Welcome Back</h2>
      </div>
      <div class="flex justify-center align-center welcome-icon-wrapper">
        <div
          class="welcome-icon pointer"
          @click="router.push({ name: 'UserAccount' })"
          role="button"
          aria-label="User Account"
        >
          <font-awesome-icon :icon="['fas', 'user']" />
        </div>
      </div>
    </div>
    <div class="dashboard-view view">
      <div class="wrapper-card">
        <div class="header-wrapper text-center">
          <h3>Recent Transactions</h3>
        </div>
        <div
          v-for="transaction in filteredTransactions"
          :key="transaction"
          class="recent-transactions"
        >
          <div class="icon">
            <span class="icon-wrapper">
              <font-awesome-icon
                :icon="
                  transaction.transactionCategory &&
                  transaction.transactionCategory.icon
                    ? parseIconString(transaction.transactionCategory.icon)
                    : 'fa-solid fa-tag'
                "
                :style="{
                  color:
                    transaction.transactionCategory &&
                    transaction.transactionCategory.iconColor
                      ? transaction.transactionCategory.iconColor
                      : '#86c24c',
                }"
                alt="transaction category icon"
              ></font-awesome-icon>
            </span>
          </div>
          <div class="name">{{ transaction.displayName }}</div>
          <div class="amount">{{ transaction.amount }}</div>
          <div class="date">{{ transaction.transactionDate }}</div>
        </div>
        <div class="text-center mt-4">
          <button
            class="teal-outline-button"
            @click="router.push({ name: 'Transactions' })"
            aria-label="View All Transactions"
            role="button"
          >
            View All <font-awesome-icon :icon="['fas', 'arrow-right-long']" />
          </button>
        </div>
      </div>

      <div class="wrapper-card">
        <div
          v-show="topCategoriesGraphData && topCategoriesGraphData.length >= 1"
        >
          <div class="header-wrapper text-center">
            <h3>Top 5 Categories</h3>
          </div>
          <div class="light-card-content">
            <div class="top-cat-wrapper">
              <canvas ref="chartRef">Chart element</canvas>
            </div>
          </div>
          <div class="text-center mt-4">
            <button
              class="teal-outline-button"
              @click="router.push({ name: 'Budget' })"
            >
              View All <font-awesome-icon :icon="['fas', 'arrow-right-long']" />
            </button>
          </div>
        </div>
        <div
          v-show="!topCategoriesGraphData || topCategoriesGraphData.length < 1"
          class="top-cat-wrapper"
        >
          Categorize your transactons and see how you're spending your money.
          <div class="text-center mt-4">
            <button
              class="teal-outline-button"
              @click="router.push({ name: 'Transactions' })"
            >
              Go to transactions
              <font-awesome-icon :icon="['fas', 'arrow-right-long']" />
            </button>
          </div>
        </div>
      </div>

      <div class="wrapper-card">
        <div class="header-wrapper text-center">
          <h3>Current Net Worth</h3>
        </div>
        <div class="asset-liability-overview">
          <div class="overview-card">
            <div class="text">Total Assets</div>
            <div class="net-worth-value">
              {{ formatMoney(netWorth.totalAssets) }}
            </div>
          </div>
          <div class="overview-card">
            <div class="text">Total Liabilities</div>
            <div class="net-worth-value">
              {{ formatMoney(netWorth.totalLiabilities) }}
            </div>
          </div>
        </div>
        <div class="ps-2 pe-2">
          <div class="overview-card mt-1 ps-2 pe-2">
            <div class="text">Total Net Worth</div>
            <div class="net-worth-value">
              {{ formatMoney(netWorth.netWorthTotal) }}
            </div>
          </div>
        </div>
        <div class="text-center mt-4">
          <button
            class="teal-outline-button"
            @click="router.push({ name: 'NetWorth' })"
          >
            View Details
            <font-awesome-icon :icon="['fas', 'arrow-right-long']" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../store.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import { formatMoney } from '../utils/formatMoney.js';

import {
  Chart,
  BarController,
  LinearScale,
  CategoryScale,
  BarElement,
} from 'chart.js';
Chart.register(BarController, LinearScale, CategoryScale, BarElement);

export default {
  name: 'User-Dashboard',
  components: {
    SkeletonLoader,
  },
  setup() {
    const router = useRouter();
    const store = useMyStore();
    const user = ref(store.user);
    let chartRef = ref(null);
    const userCategories = [];
    let filteredTransactions = ref([]);
    let topCategoriesGraphData = ref([]);
    let netWorth = ref(0);
    const loading = ref(true);

    onMounted(() => {
      console.log('hits mounted dashboard');
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      let unwatch;
      unwatch = watch(
        () => store.userLoggedIn,
        (newValue) => {
          if (newValue) {
            store
              .getUserDashboard()
              .then((response) => {
                loading.value = false;
                filteredTransactions.value =
                  response.data.mostRecentTransactions;
                topCategoriesGraphData.value = response.data.topFiveCategories;
                netWorth.value = response.data.netWorth;
                if (
                  topCategoriesGraphData.value &&
                  topCategoriesGraphData.value.length >= 1
                ) {
                  nextTick(() => {
                    createTopCategoriesChart();
                  });
                }
              })
              .catch((error) => {
                console.error('Error fetching dashboard data:', error);
                loading.value = false;
              })
              .finally(() => {
                if (unwatch) unwatch();
              });
          }
        },
        { immediate: true }
      );
    });

    const prepareChartData = () => {
      const data = topCategoriesGraphData.value.map((item) => ({
        categoryLabel: item.category.name || 'Uncategorized',
        amount: item.categorySpent || 0,
        backgroundColor: item.category.iconColor || '#86c24c',
      }));

      while (data.length < 5) {
        data.push({
          categoryLabel: '',
          amount: 0,
          backgroundColor: 'transparent',
        });
      }

      return data;
    };

    const createTopCategoriesChart = () => {
      if (!chartRef.value) {
        console.error('Chart reference is null. Canvas element not found.');
        return;
      }

      const topCategoryGraphData = prepareChartData();

      chartRef.value = new Chart(chartRef.value, {
        type: 'bar',
        data: {
          labels: topCategoryGraphData.map((item) => item.categoryLabel),
          datasets: [
            {
              data: topCategoryGraphData.map((item) =>
                item.amount ? item.amount : 0
              ),
              backgroundColor: topCategoryGraphData.map((item) =>
                item.backgroundColor ? item.backgroundColor : '#86c24c'
              ),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              border: {
                display: false,
              },
              ticks: {
                display: false,
                color: store.darkTheme ? 'white' : 'black',
              },
              grid: {
                display: false,
                drawBorder: false,
              },
            },
            x: {
              ticks: {
                color: store.darkTheme ? 'white' : 'black',
              },
              grid: {
                display: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
            },
          },
          elements: {
            bar: {
              barPercentage: 0.5,
              categoryPercentage: 0.5,
              borderRadius: 50,
            },
          },
        },
      });
    };

    const parseIconString = (iconString) => {
      if (iconString && iconString.includes(' ')) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return ['fa-solid', 'fa-tag'];
    };

    return {
      router,
      store,
      chartRef,
      userCategories,
      createTopCategoriesChart,
      filteredTransactions,
      parseIconString,
      topCategoriesGraphData,
      netWorth,
      formatMoney,
      loading,
      user,
    };
  },
};
</script>

<style scoped>
.dashboard-view {
  padding: 1em;
  background: transparent;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1em;
}

.header-wrapper {
  padding: 0 1em 1em 1em;
  display: flex;
  justify-content: space-between;
}

.welcome {
  display: grid;
  grid-template-columns: 2fr 1fr;
  height: 120px;
}

.light-theme .welcome {
  background: #004751;
}

.dark-theme .welcome {
  background: #004751;
}

.welcome-text > * {
  color: white;
}

.welcome h2 {
  font-weight: 700;
}

.welcome p {
  font-weight: light;
  margin-bottom: 0;
  margin-top: 0;
}

.welcome .welcome-text {
  align-self: center;
  padding-left: 1em;
}

.welcome-text h2 {
  line-height: 1em;
}

.welcome-icon {
  font-size: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: black;
  background: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.recent-transactions {
  display: grid;
  grid-template-columns: 1fr 2fr 2fr;
  grid-template-areas:
    'icon name amount'
    'icon date amount';
  padding: 0 1em;
  row-gap: 0.25em;
  margin-bottom: 1em;
}

.icon {
  grid-area: icon;
  display: flex;
  align-items: center;
}

.name {
  grid-area: name;
}

.dark-theme .name {
  color: #d4d2d2;
}

.amount {
  grid-area: amount;
  display: flex;
  justify-content: end;
  align-items: center;
  font-weight: 500;
}

.light-theme .amount {
  color: #515a65;
}

.dark-theme .amount {
  color: #ffffff;
}

.date {
  grid-area: date;
  font-size: 12px;
}

.light-theme .date {
  color: #515a65;
}

.dark-theme .date {
  color: #b0b0b0;
}

.icon-wrapper {
  display: inline-block;
  position: relative;
  width: 40px;
  height: 40px;
}

.icon-wrapper i {
  display: block;
  position: relative;
  color: coral;
  font-size: 19px;
  text-align: center;
  line-height: 40px;
}

.top-cat-wrapper {
  width: 75%;
  margin: 0 auto;
  min-height: 300px;
}

.welcome-icon-wrapper {
  justify-content: end;
  margin-right: 1em;
}

.asset-liability-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5em;
  padding: 0 1em;
}

@media screen and (min-width: 992px) {
  .light-theme .welcome {
    background: white;
    color: #004751;
    box-shadow: 5px 0 10px 5px rgb(0, 0, 0, 0.1);
  }

  .welcome p {
    color: #004751;
  }

  .welcome h2 {
    color: #004751;
  }

  .welcome .welcome-icon {
    box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
    height: 60px;
    width: 60px;
    font-size: 30px;
  }
}

@media screen and (min-width: 1200px) {
  .dashboard-view {
    grid-template-columns: 1fr 1fr;
  }
}
</style> -->
